import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { getMyProviderListings, updateMyProviderListing } from '@/lib/api';
import { toast } from 'sonner';
import { Store, Pause, Play, Pencil, X, Save, Phone, MapPin, Loader2, PlusCircle } from 'lucide-react';

const STATUS_BADGE = {
  active: { label: 'Live', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending_payment: { label: 'Payment pending', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const PartnerDashboardPage = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setListings(await getMyProviderListings()); }
    catch { toast.error('Could not load your listings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (l) => {
    setEditingId(l.provider_id);
    setEditForm({
      name: l.name || '', phone: l.phone || '', experience: l.experience || '',
      city: l.city || '', state: l.state || '', service_area: l.service_area || '',
      description: l.description || '', website: l.website || '',
    });
  };
  const setF = (k, v) => setEditForm((p) => ({ ...p, [k]: v }));

  const saveEdit = async (id) => {
    setBusyId(id);
    try {
      const updated = await updateMyProviderListing(id, editForm);
      setListings((prev) => prev.map((l) => (l.provider_id === id ? updated : l)));
      setEditingId(null);
      toast.success('Listing updated');
    } catch (err) { toast.error(err.message || 'Could not save'); }
    finally { setBusyId(null); }
  };

  const toggleStatus = async (l) => {
    const next = l.status === 'active' ? 'paused' : 'active';
    setBusyId(l.provider_id);
    try {
      const updated = await updateMyProviderListing(l.provider_id, { status: next });
      setListings((prev) => prev.map((x) => (x.provider_id === l.provider_id ? updated : x)));
      toast.success(next === 'active' ? 'Listing is now live' : 'Listing paused');
    } catch (err) { toast.error(err.message || 'Could not update'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-20">
      <Seo title="My listings — Bharat Estate Partners" path="/partner" />
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold mb-2"><Store className="h-4 w-4" /> Partner dashboard</span>
          <h1 className="text-2xl md:text-3xl font-extrabold">Manage your listings</h1>
          <p className="text-white/70 mt-1 text-sm">Signed in as {user?.email}. Edit your details or pause a listing anytime.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-8" data-testid="partner-dashboard">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="partner-empty">
            <p className="text-slate-600">No listings under <span className="font-semibold">{user?.email}</span> yet.</p>
            <p className="text-slate-500 text-sm mt-1">Make sure you registered with this same email address.</p>
            <Link to="/become-a-partner"><Button className="rounded-full bg-[#D4AF37] text-[#0F172A] font-bold mt-5" data-testid="partner-list-cta"><PlusCircle className="h-4 w-4 mr-1.5" /> List my business</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map((l) => {
              const badge = STATUS_BADGE[l.status] || STATUS_BADGE.pending_payment;
              const editing = editingId === l.provider_id;
              const paid = l.payment_status !== 'pending';
              return (
                <div key={l.provider_id} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid={`partner-listing-${l.provider_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#0F172A]">{l.name}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`} data-testid={`partner-status-${l.provider_id}`}>{badge.label}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{l.category_label}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mt-2">
                        <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {l.phone}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {l.service_area || l.city}</span>
                      </div>
                    </div>
                    {!editing && (
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => startEdit(l)} data-testid={`partner-edit-${l.provider_id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                        {paid && (
                          <Button variant="outline" size="sm" disabled={busyId === l.provider_id} className="rounded-full" onClick={() => toggleStatus(l)} data-testid={`partner-toggle-${l.provider_id}`}>
                            {l.status === 'active' ? <><Pause className="h-3.5 w-3.5 mr-1" /> Pause</> : <><Play className="h-3.5 w-3.5 mr-1" /> Resume</>}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {editing && (
                    <div className="mt-5 border-t border-slate-100 pt-5 space-y-4" data-testid={`partner-edit-form-${l.provider_id}`}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div><Label>Business name</Label><Input value={editForm.name} onChange={(e) => setF('name', e.target.value)} className="mt-1" data-testid="edit-name" /></div>
                        <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setF('phone', e.target.value)} className="mt-1" data-testid="edit-phone" /></div>
                        <div><Label>Experience</Label><Input value={editForm.experience} onChange={(e) => setF('experience', e.target.value)} className="mt-1" data-testid="edit-experience" /></div>
                        <div><Label>Website</Label><Input value={editForm.website} onChange={(e) => setF('website', e.target.value)} placeholder="https://" className="mt-1" data-testid="edit-website" /></div>
                        <div><Label>City</Label><Input value={editForm.city} onChange={(e) => setF('city', e.target.value)} className="mt-1" data-testid="edit-city" /></div>
                        <div><Label>State</Label><Input value={editForm.state} onChange={(e) => setF('state', e.target.value)} className="mt-1" data-testid="edit-state" /></div>
                        <div className="sm:col-span-2"><Label>Area / locality</Label><Input value={editForm.service_area} onChange={(e) => setF('service_area', e.target.value)} className="mt-1" data-testid="edit-area" /></div>
                        <div className="sm:col-span-2"><Label>About your service</Label><Textarea value={editForm.description} onChange={(e) => setF('description', e.target.value)} rows={3} className="mt-1" data-testid="edit-description" /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button disabled={busyId === l.provider_id} className="rounded-full bg-[#0F172A] text-white" onClick={() => saveEdit(l.provider_id)} data-testid={`partner-save-${l.provider_id}`}>
                          {busyId === l.provider_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
                        </Button>
                        <Button variant="ghost" className="rounded-full" onClick={() => setEditingId(null)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboardPage;
